import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowRight } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import GoogleReviews from "@/components/GoogleReviews";
import FeaturedVideo from "@/components/FeaturedVideo";
import FeaturedVehicles from "@/components/FeaturedVehicles";
import WhyChooseUs from "@/components/WhyChooseUs";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/vehicles?featured=true`);
        setFeaturedVehicles(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching featured vehicles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`}
      data-testid="landing-page"
    >
      <Navbar />
      <HeroSection />
      <GoogleReviews />
      <FeaturedVideo />
      <FeaturedVehicles vehicles={featuredVehicles} loading={loading} />
      
      {/* Pre-Qualification CTA Section */}
      <section className={`py-20 px-6 md:px-12 ${
        isDark 
          ? 'bg-gradient-to-b from-[#050505] to-blue-950/10' 
          : 'bg-gradient-to-b from-[#FAFAFA] to-red-50'
      }`}>
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <div className={`inline-flex items-center justify-center w-16 h-16 border mb-6 ${
              isDark 
                ? 'bg-blue-500/10 border-blue-500/30' 
                : 'bg-red-50 border-red-200'
            }`}>
              <Shield className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-red-500'}`} />
            </div>
            
            <h2 className={`text-3xl md:text-4xl font-unbounded font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {t('preQualCTA.title')}
            </h2>
            
            <p className={`text-lg font-manrope mb-8 ${
              isDark ? 'text-white/60' : 'text-gray-600'
            }`}>
              {t('preQualCTA.description')}
            </p>
            
            <button
              onClick={() => navigate('/prequalification')}
              className={`group relative inline-flex items-center gap-3 px-8 py-4 font-manrope font-semibold text-lg transition-all overflow-hidden ${
                isDark 
                  ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
              data-testid="prequal-cta-btn"
            >
              <span className="relative z-10">{t('preQualCTA.buttonText')}</span>
              <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                isDark 
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700' 
                  : 'bg-gradient-to-r from-red-700 to-red-800'
              }`}></div>
            </button>
            
            <p className="text-2xl text-green-500 font-manrope font-semibold mt-5 flex items-center justify-center gap-3 max-w-3xl mx-auto leading-snug text-center">
              <span className="w-3 h-3 bg-green-500 rounded-full shrink-0"></span>
              {t('preQualCTA.noCreditImpact')}
            </p>
          </motion.div>
        </div>
      </section>

      <WhyChooseUs />
      <ContactSection />
      <Footer />
    </motion.div>
  );
}
